import { CalendarDate } from "@sp-crm/core";
import { CalendarDateInput } from "components/ui/calendar-date";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";

export interface DatePreset {
    label: string;
    getRange: () => { start: CalendarDate; end: CalendarDate };
}

export const getDatePresets = (): DatePreset[] => {
    const today = CalendarDate.today;
    return [
        {
            label: "Year to Date",
            getRange: () => ({
                start: new CalendarDate(today.year, 1, 1),
                end: today,
            }),
        },
        {
            label: "This Month",
            getRange: () => ({
                start: new CalendarDate(today.year, today.month, 1),
                end: today,
            }),
        },
        {
            label: "Last Month",
            getRange: () => {
                const lastMonth =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 12, 1)
                        : new CalendarDate(today.year, today.month - 1, 1);
                const endDay = new Date(lastMonth.year, lastMonth.month, 0).getDate();
                return {
                    start: lastMonth,
                    end: new CalendarDate(lastMonth.year, lastMonth.month, endDay),
                };
            },
        },
        {
            label: "This Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                return {
                    start: new CalendarDate(today.year, q * 3 + 1, 1),
                    end: today,
                };
            },
        },
        {
            label: "Last Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                const prevQ = q === 0 ? 3 : q - 1;
                const year = q === 0 ? today.year - 1 : today.year;
                const startMonth = prevQ * 3 + 1;
                const endMonth = startMonth + 2;
                const endDay = new Date(year, endMonth, 0).getDate();
                return {
                    start: new CalendarDate(year, startMonth, 1),
                    end: new CalendarDate(year, endMonth, endDay),
                };
            },
        },
        {
            label: "Last 12 Months",
            getRange: () => {
                const start =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 1, today.day)
                        : new CalendarDate(today.year - 1, today.month, today.day);
                return { start, end: today };
            },
        },
        {
            label: "Last Year",
            getRange: () => ({
                start: new CalendarDate(today.year - 1, 1, 1),
                end: new CalendarDate(today.year - 1, 12, 31),
            }),
        },
    ];
};

const CUSTOM_VALUE = "__custom__";

const DatePresetButtons: React.FC<{
    activePreset: string | null;
    onSelect: (label: string, start: CalendarDate, end: CalendarDate) => void;
}> = ({ activePreset, onSelect }) => {
    const presets = React.useMemo(() => getDatePresets(), []);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const label = e.target.value;
        if (label === CUSTOM_VALUE) return;
        const preset = presets.find(p => p.label === label);
        if (preset) {
            const range = preset.getRange();
            onSelect(preset.label, range.start, range.end);
        }
    };

    return (
        <>
            <div className="hidden xl:flex flex-wrap gap-1">
                {presets.map(p => (
                    <SecondaryCompactButton
                        key={p.label}
                        selected={activePreset === p.label}
                        onClick={() => {
                            const range = p.getRange();
                            onSelect(p.label, range.start, range.end);
                        }}>
                        {p.label}
                    </SecondaryCompactButton>
                ))}
            </div>
            <Select
                className="xl:hidden mt-2"
                label="Date Range"
                value={activePreset ?? CUSTOM_VALUE}
                onChange={handleSelectChange}>
                {activePreset === null ? (
                    <option value={CUSTOM_VALUE}>Custom</option>
                ) : null}
                {presets.map(p => (
                    <option key={p.label} value={p.label}>
                        {p.label}
                    </option>
                ))}
            </Select>
        </>
    );
};

export interface DateRangeValue {
    startDate: CalendarDate;
    endDate: CalendarDate;
    activePreset: string | null;
}

export const DateRangeControl: React.FC<{
    value: DateRangeValue;
    onChange: (value: DateRangeValue) => void;
}> = ({ value, onChange }) => {
    return (
        <div className="md:flex md:space-x-3 items-end">
            <div className="md:flex-shrink-0">
                <CalendarDateInput
                    label="Start"
                    value={value.startDate}
                    onChange={v => {
                        if (v) {
                            onChange({
                                ...value,
                                startDate: v,
                                activePreset: null,
                            });
                        }
                    }}
                />
            </div>
            <div className="md:flex-shrink-0">
                <CalendarDateInput
                    label="End"
                    value={value.endDate}
                    onChange={v => {
                        if (v) {
                            onChange({
                                ...value,
                                endDate: v,
                                activePreset: null,
                            });
                        }
                    }}
                />
            </div>
            <DatePresetButtons
                activePreset={value.activePreset}
                onSelect={(label, start, end) => {
                    onChange({
                        startDate: start,
                        endDate: end,
                        activePreset: label,
                    });
                }}
            />
        </div>
    );
};
